import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl, isTelegramWebApp, optimizeWebAppInput } from '../config';
import './CreateActivityForm.css';

// Компонент формы создания активности
const CreateActivityForm = ({ telegramId, onSuccess, onCancel }) => {
    // Ссылки на поля ввода
    const titleInputRef = useRef(null);
    const descriptionInputRef = useRef(null);
    
    // Состояния формы
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [expiryDays, setExpiryDays] = useState(7); // По умолчанию 7 дней
    
    // Состояния UI
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [debugInfo, setDebugInfo] = useState('');

    // Определяем, запущены ли мы в Telegram WebApp
    const [isWebApp, setIsWebApp] = useState(false);

    // Эффект для определения WebApp и настройки ввода полей
    useEffect(() => {
        // Проверяем, запущены ли мы в Telegram WebApp
        const isInWebApp = isTelegramWebApp();
        setIsWebApp(isInWebApp);
        
        if (isInWebApp) {
            addDebugInfo('Режим Telegram WebApp активирован, применяются исправления для полей ввода');
        }
    }, []);

    // Добавление функции логирования для отладки
    const addDebugInfo = (message) => {
        console.log(`[CreateActivity] ${message}`);
        setDebugInfo(prev => `${prev}\n${new Date().toISOString()}: ${message}`);
    };
    
    // Пользовательские обработчики ввода с оптимизацией для WebApp
    const handleTitleClick = (e) => {
        if (isWebApp) {
            addDebugInfo('Клик в поле заголовка, активируем ввод');
            optimizeWebAppInput(e.target);
        }
    };
    
    const handleDescriptionClick = (e) => {
        if (isWebApp) {
            addDebugInfo('Клик в поле описания, активируем ввод');
            optimizeWebAppInput(e.target);
        }
    };

    // Обработчик отправки формы
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Валидация
        if (!title.trim()) {
            setError('Пожалуйста, укажите название активности');
            return;
        }
        
        if (!description.trim()) {
            setError('Пожалуйста, добавьте описание активности');
            return;
        }
        
        if (!telegramId) {
            setError('Ошибка: Не удалось определить ID пользователя');
            addDebugInfo(`Ошибка: telegramId отсутствует (${telegramId})`);
            return;
        }

        addDebugInfo(`Начало создания активности для пользователя ${telegramId}`);
        addDebugInfo(`Данные: title="${title}", description=${description.length} символов, expiryDays=${expiryDays}`);
        
        setLoading(true);
        setError(null);
        
        try {
            // Подготавливаем данные
            const formData = new FormData();
            formData.append('telegram_id', telegramId);
            formData.append('title', title);
            formData.append('description', description);
            
            // Расчет даты истечения на основе выбранного количества дней
            const expiryDate = new Date();
            expiryDate.setDate(expiryDate.getDate() + expiryDays);
            // Добавляем 'Z' в конец, чтобы указать UTC формат
            const formattedDate = expiryDate.toISOString();
            
            formData.append('expires_at', formattedDate);
            addDebugInfo(`expires_at установлен на ${expiryDays} дней вперед: ${formattedDate}`);
            
            // Отправляем запрос
            addDebugInfo(`Отправка запроса на: ${getApiUrl('/api/activities/create')}`);
            const response = await fetch(getApiUrl('/api/activities/create'), {
                method: 'POST',
                body: formData,
                // Не включаем заголовки Content-Type, так как FormData установит их автоматически
                headers: {
                    'Accept': 'application/json'
                },
                // Не включаем credentials, так как на бэкенде добавили csrf_exempt
                credentials: 'same-origin'
            });
            
            addDebugInfo(`Получен ответ: статус ${response.status}`);
            
            // Проверяем ответ, пытаемся получить текст ответа в случае ошибки
            if (!response.ok) {
                let errorMessage = `Ошибка при создании активности. Код: ${response.status}`;
                try {
                    const contentType = response.headers.get("content-type");
                    addDebugInfo(`Content-Type ответа: ${contentType}`);
                    
                    // Получаем полные заголовки для отладки
                    let headersDebug = "";
                    response.headers.forEach((value, key) => {
                        headersDebug += `${key}: ${value}\n`;
                    });
                    addDebugInfo(`Все заголовки ответа:\n${headersDebug}`);
                    
                    if (contentType && contentType.includes("application/json")) {
                        const data = await response.json();
                        errorMessage = data.error || errorMessage;
                        addDebugInfo(`Получена JSON ошибка: ${JSON.stringify(data)}`);
                    } else {
                        const text = await response.text();
                        addDebugInfo(`Получен текстовый ответ: ${text}`);
                        console.error("Ответ сервера не в формате JSON:", text);
                        // Пытаемся проверить, не является ли ответ HTML с сообщением об ошибке Django
                        if (text.includes("<title>") && text.includes("Django")) {
                            errorMessage = "Ошибка на сервере Django. Проверьте логи сервера.";
                        } else {
                            errorMessage = "Ошибка сервера. Попробуйте позже.";
                        }
                    }
                } catch (parseError) {
                    console.error("Ошибка при обработке ответа:", parseError);
                    addDebugInfo(`Ошибка при обработке ответа: ${parseError.message}`);
                }
                throw new Error(errorMessage);
            }
            
            // Получаем данные ответа
            const data = await response.json();
            addDebugInfo(`Активность успешно создана. Ответ: ${JSON.stringify(data)}`);
            
            // Устанавливаем состояние успеха и сбрасываем форму
            setSuccess(true);
            setTitle('');
            setDescription('');
            
            // Если есть функция обратного вызова при успехе, вызываем ее
            if (onSuccess) {
                setTimeout(() => {
                    onSuccess(data);
                }, 1500);
            }
            
        } catch (err) {
            console.error("Ошибка при создании активности:", err);
            setError(err.message);
            addDebugInfo(`Ошибка: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="create-activity-form">
            <div className="form-header">
                <h2>Создание активности</h2>
                <p className="form-description">
                    Создайте объявление, которое будет видно всем пользователям. 
                    Активность автоматически исчезнет через выбранный период времени.
                </p>
            </div>
            
            {error && (
                <div className="error-message">
                    <p>⚠️ {error}</p>
                </div>
            )}
            
            {success ? (
                <div className="success-message">
                    <p>✅ Активность успешно создана!</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="title">Название активности*</label>
                        <input
                            type="text"
                            id="title"
                            ref={titleInputRef}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onClick={handleTitleClick}
                            placeholder="Например: Ищу партнера для игры в теннис"
                            maxLength={50}
                            required
                            autoComplete="off" // <--- ДОБАВЛЕНО
                        />
                        <p className="character-count">{title.length}/50 символов</p>
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="description">Описание*</label>
                        <textarea
                            id="description"
                            ref={descriptionInputRef}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            onClick={handleDescriptionClick}
                            placeholder="Подробно опишите вашу активность..."
                            rows={5}
                            maxLength={500}
                            required
                        ></textarea>
                        <p className="character-count">{description.length}/500 символов</p>
                    </div>
                    
                    <div className="form-group">
                        <label>Актуально до</label>
                        <div className="expiry-options">
                            <div className="expiry-radio-group">
                                <div className="expiry-radio-option">
                                    <input 
                                        type="radio" 
                                        id="expiry-1day"
                                        name="expiry-days"
                                        checked={expiryDays === 1}
                                        onChange={() => setExpiryDays(1)}
                                    />
                                    <label htmlFor="expiry-1day">1 день</label>
                                </div>
                                
                                <div className="expiry-radio-option">
                                    <input 
                                        type="radio" 
                                        id="expiry-3days"
                                        name="expiry-days"
                                        checked={expiryDays === 3}
                                        onChange={() => setExpiryDays(3)}
                                    />
                                    <label htmlFor="expiry-3days">3 дня</label>
                                </div>
                                
                                <div className="expiry-radio-option">
                                    <input 
                                        type="radio" 
                                        id="expiry-7days"
                                        name="expiry-days"
                                        checked={expiryDays === 7}
                                        onChange={() => setExpiryDays(7)}
                                    />
                                    <label htmlFor="expiry-7days">7 дней</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="form-actions">
                        <button 
                            type="button" 
                            className="button-secondary"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            Отмена
                        </button>
                        
                        <button 
                            type="submit" 
                            className="button-primary"
                            disabled={loading}
                        >
                            {loading ? 'Создание...' : 'Создать активность'}
                        </button>
                    </div>
                    
                    {/* Закомментировано отображение отладочной информации 
                    {process.env.NODE_ENV === 'development' && debugInfo && (
                        <div className="debug-info">
                            <pre>{debugInfo}</pre>
                        </div>
                    )}
                    */}
                </form>
            )}
        </div>
    );
};

export default CreateActivityForm; 